'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSession } from '@/providers/session/SessionProvider';
import { useAuth } from '@/providers/auth/AuthProvider';
import SignIn, { Credential } from '@/pages/Welcome/components/SignIn';
import SignUp from './components/SignUp';
import { useGoogleLogin } from '@react-oauth/google';
import { useGoogleAuth } from '@/providers/auth/GoogleAuthProvider';
import { useNotifications } from '@toolpad/core';
import { capture } from '@/handlers/error';
import { useCleverAuth } from '@/providers/auth/CleverAuthProvider';
import { AuthProviderType } from '@/types/auth';
import { usePreference } from '@/providers/preference/PreferenceProvider';
import { useTranslation } from 'react-i18next';
import { PreferenceKey } from '@/providers/preference/type';

export default function WelcomePage() {
    // Providers
    const { t } = useTranslation();
    const notifications = useNotifications();
    const { login, register, requestResetPassword } = useAuth();
    const { authorize: authorizeWithGoogle } = useGoogleAuth();
    const googleLogin = useGoogleLogin({
        onSuccess: (response) => {
            setLoading(true);
            authorizeWithGoogle(response.code)
                .then(async (session) => {
                    await setSession(session.token);
                    // Redirect based on user role
                    const redirectPath = session.user?.role === 'student' ? '/account' : '/lessons';
                    navigate(redirectPath, { replace: true });
                })
                .catch((error) => {
                    capture(error);
                    notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                })
                .finally(() => {
                    setLoading(false);
                });
        },
        scope: 'profile email',
        flow: 'auth-code',
    });
    const { authorize: authorizeWithClever } = useCleverAuth();
    const { setSession } = useSession();
    const { set: setPreference, get: getPreference } = usePreference();
    const navigate = useNavigate();

    // UI state
    const [state, setState] = useState<'signUp' | 'signIn'>('signIn');
    const [loading, setLoading] = useState(false);

    // params
    const url = useMemo(() => new URL(window.location.href), []);
    const params = useMemo(() => url.searchParams, [url.searchParams]);
    const code = useMemo(() => params.get('code'), [params]);

    // Workaround: this ref is using as a workaround to prevent the useEffect handler from running twice in React 18 StrictMode
    const isOAuthHandled = useRef(false);

    const handleSignIn = async (provider: AuthProviderType, credential?: Credential, remember?: boolean) => {
        try {
            switch (provider) {
                case 'credential':
                    if (credential) {
                        const session = await login(credential.username, credential.password);
                        await setSession(session.token);
                        if (remember) {
                            setPreference(PreferenceKey.Username, credential.username);
                        } else {
                            setPreference(PreferenceKey.Username, null);
                        }
                        // Redirect based on user role
                        const redirectPath = session.user?.role === 'student' ? '/account' : '/lessons';
                        navigate(redirectPath, { replace: true });
                        return {};
                    } else {
                        return { error: `Missing credential` };
                    }
                case 'google':
                    googleLogin();
                    return {};
                case 'clever':
                    window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${window.location.origin}/welcome&client_id=9bb51623462f47dd0659`;
                    return {};
                default:
                    return { error: `Unsupported provider ${provider}` };
            }
        } catch (error) {
            return { error: error instanceof Error ? error.message : `${error}` };
        }
    };

    const handleSignUp = async (provider: AuthProviderType, email?: string) => {
        try {
            switch (provider) {
                case 'credential':
                    if (email) {
                        await register(email);
                        notifications.show(`${t('welcome.verifyEmail.message')} ${email}`, { severity: 'success', autoHideDuration: 10000 });
                        return {};
                    } else {
                        return { error: `Missing email` };
                    }
                case 'google':
                    googleLogin();
                    return {};
                case 'clever':
                    window.location.href = `https://clever.com/oauth/authorize?response_type=code&redirect_uri=${window.location.origin}/welcome&client_id=9bb51623462f47dd0659`;
                    return {};
                default:
                    return { error: `Unsupported provider ${provider}` };
            }
        } catch (error) {
            return { error: error instanceof Error ? error.message : `${error}` };
        }
    };

    const handleRequestResetPassword = async (email: string) => {
        try {
            await requestResetPassword(email);
            notifications.show(`A password reset link is sent to ${email}`, { severity: 'success', autoHideDuration: 5000 });
        } catch (error) {
            notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
        }
    };

    // OAuth redirect handler
    useEffect(() => {
        if (isOAuthHandled.current === false) {
            isOAuthHandled.current = true;
            // only support clever login for oauth redirect for now
            if (document.referrer.includes('clever.com') && code) {
                setLoading(true);
                authorizeWithClever(code)
                    .then(async (session) => {
                        await setSession(session.token);
                        // Redirect based on user role
                        const redirectPath = session.user?.role === 'student' ? '/account' : '/lessons';
                        navigate(redirectPath, { replace: true });
                    })
                    .catch((error) => {
                        capture(error);
                        notifications.show(error instanceof Error ? error.message : `${error}`, { severity: 'error', autoHideDuration: 5000 });
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        }
    }, [authorizeWithClever, code, navigate, notifications, setSession]);

    return (
        <>
            {state === 'signIn' && (
                <SignIn
                    onSignUp={() => setState('signUp')}
                    onSignIn={handleSignIn}
                    onRequestResetPassword={handleRequestResetPassword}
                    loading={loading}
                    defaultEmail={getPreference(PreferenceKey.Username) || ''}
                />
            )}
            {state === 'signUp' && <SignUp onSignIn={() => setState('signIn')} onSignUp={handleSignUp} loading={loading} />}
        </>
    );
}
